import { Corners } from "../common/components/image-types";
import { AppKind } from "../common/components/SerializedApp";
import type { ActionDescription, Description, PropertyDescription } from "../common/description";
import {
    ActionKind,
    ArrayScreenFormat,
    makeEmptyComponentDescription,
    makeInputOutputTables,
} from "../common/description";
import { isEmptyOrUndefined } from "../support";
import { ArrayPropertyStyle, promoteAppDescriptionContext } from "../component-utils";
import { titleProperties } from "../component-utils/description-utils";
import { moodCases } from "../component-utils/mood";
import { PropertySection } from "../component-utils/property-sections";
import {
    getEnumProperty,
    getSourceColumnProperty,
    getStringProperty,
    makeActionProperty,
    makeArrayProperty,
    makeEnumProperty,
    makeStringProperty,
    makeSwitchProperty,
} from "../component-utils/property-utils";
import { PageScreenTarget } from "../wire/backends";
import { WireComponentKind } from "../wire/component-kind";
import {
    ButtonBarSize,
    CardStyle,
    CustomComponentSource,
    CustomComponentSourceTransform,
    CustomComponentWidth,
    HeroSize,
    SeparatorSize,
    UIAlignment,
    UIAspect,
    UIBackgroundStyle,
    UIButtonAppearance,
    UIImageFill,
    UIImageStyle,
    UISize,
    UIStyleVariant,
} from "../wire/types";
import { Container, FormContainer } from "./fluent-components-containers";
import type {
    DescriptionOfArrayContentInlineList,
    DescriptionOfComponent,
    FluentComponentSpecializer,
    WirePropertiesOfArrayContent,
    WirePropertiesOfComponent,
} from "./fluent-components-spec";
import { arrayContent, component, form, properties } from "./fluent-components-spec";

function makeActionWithTitleProperties<TParent>() {
    return properties
        .withParent<TParent>()
        .withCaption("title", "Go!")
        .withAction("action", { required: false, emptyByDefault: false });
}

function makeActionWithTitle(title: string, action: ActionDescription): Description {
    return {
        title: makeStringProperty(title),
        action: makeActionProperty(action),
    } as any;
}

function withContainer(
    displayName: string,
    style: UIBackgroundStyle,
    f: FluentComponentSpecializer
): FluentComponentSpecializer {
    return (desc, tables, iccc, mutatingScreenKind, inContainer) => {
        desc = f(desc, tables, iccc, mutatingScreenKind, inContainer);
        if (inContainer) {
            return desc;
        } else {
            return {
                ...makeEmptyComponentDescription(WireComponentKind.Container),
                builderDisplayName: displayName,
                containerStyle: makeEnumProperty(style),
                components: [desc],
            };
        }
    };
}

// When there's no action we remove the title as well so we don't render a
// disabled button.
const removeIfMissingInActionWithTitle = { action: ["title"] } as const;

const Button = component(WireComponentKind.Button)
    .withIcon("mt-component-button")
    .withDisplayName("Button")
    .withDocURL("pagesButton")
    .withLegacy()
    .withProperties(
        properties
            .withCaption("title", "Go!")
            .withAction("action", { required: true })
            // .withText("description")
            .withEnum(
                "appearance",
                [
                    {
                        value: UIButtonAppearance.Filled,
                        label: "Accent",
                    },
                    {
                        value: UIButtonAppearance.Bordered,
                        label: "Default",
                    },
                ],
                {
                    propertySection: PropertySection.Design,
                    displayName: "Style",
                    defaultValue: UIButtonAppearance.Bordered,
                }
            )
    );
export type WireButtonComponent = WirePropertiesOfComponent<typeof Button>;

const Hint = component(WireComponentKind.Hint)
    .withIcon("mt-component-hint")
    .withDisplayName("Hint")
    .withDescription("A callout area to write important text")
    .withDocURL("pagesHint")
    .withProperties(
        properties
            .withText("description", { defaultValue: "This is a hint." })
            .withEmoji("emoji", undefined, { isDisplayed: false })
            .withCaption("actionTitle", "Go!", { displayName: "Title", propertySection: PropertySection.Action })
            .withAction("action")
            .withEnum("mood", moodCases)
    );
export type WireHintComponent = WirePropertiesOfComponent<typeof Hint>;

// Make main title legacy
const Title = component(WireComponentKind.Hero)
    .withDisplayName("Title")
    .withIcon("mt-component-title")
    .withDescription("Page/section header with actions")
    .withDocURL("pagesTitle")
    .withProperties(
        properties
            .withText("title", {
                subcomponent: {
                    important: true,
                    name: "Content",
                },
            })
            .withText("subtitle", {
                subcomponent: {
                    important: true,
                    name: "Content",
                },
            })
            .withImage("image", {
                subcomponent: {
                    important: true,
                    name: "Image",
                },
            })
            .withText("titleEmphasized", {
                displayName: "Emphasis",
                emptyByDefault: true,
                preferredNames: titleProperties,
                subcomponent: {
                    important: true,
                    name: "Content",
                },
            })
            .withEnum(
                "size",
                [
                    {
                        label: "XSmall",
                        value: HeroSize.XSmall,
                    },
                    {
                        label: "Small",
                        value: HeroSize.Small,
                    },
                    {
                        label: "Medium",
                        value: HeroSize.Medium,
                    },
                    {
                        label: "Large",
                        value: HeroSize.Large,
                    },
                    {
                        label: "XLarge",
                        value: HeroSize.XLarge,
                    },
                ],
                {
                    propertySection: PropertySection.Design,
                    defaultValue: HeroSize.Medium,
                    subcomponent: {
                        important: true,
                        name: "Content",
                    },
                }
            )
            .withEnum("imageCorners", [Corners.Circle, Corners.Rounded], {
                displayName: "Image Style",
                propertySection: PropertySection.Design,
                when: d => d.image !== undefined && getStringProperty(d.image) !== "",
                subcomponent: {
                    important: true,
                    name: "Image",
                },
            })
            .withArray("buttons", makeActionWithTitleProperties(), {
                propertySection: { name: "Actions", order: 4 },
                style: ArrayPropertyStyle.ActionArray,
                specialItems: 2,
                allowEmpty: true,
                removeIfMissing: removeIfMissingInActionWithTitle,
                subcomponent: {
                    important: true,
                    name: "Actions",
                },
            })
    )
    .withSpecialCases(false, [
        [
            {
                name: "Simple",
                description: "A simple Title",
                group: "Title",
                appKinds: AppKind.Page,
            },
            desc => ({ ...desc, subtitle: undefined, image: undefined, size: makeEnumProperty(HeroSize.Small) }),
        ],
        [
            {
                name: "Profile",
                description: "A profile Title",
                group: "Title",
                img: "mt-component-title-profile",
                appKinds: AppKind.Page,
            },
            withContainer("Profile Title", UIBackgroundStyle.Dark, desc => ({
                ...desc,
                size: makeEnumProperty(HeroSize.Medium),
            })),
        ],
        [
            {
                name: "Hero",
                description: "A Hero Title",
                img: "mt-component-title-hero",
                group: "Title",
                appKinds: AppKind.Page,
            },
            withContainer("Hero Title", UIBackgroundStyle.Accent, desc => ({
                ...desc,
                image: undefined,
                size: makeEnumProperty(HeroSize.XLarge),
                actions: makeArrayProperty([
                    {
                        title: makeStringProperty("Go!"),
                        action: makeActionProperty({
                            kind: ActionKind.ShowToast,
                        }),
                    },
                ]),
            })),
        ],
        [
            {
                name: "Action",
                description: "An action Title",
                img: "mt-component-title-action",
                group: "Title",
                appKinds: AppKind.Page,
            },
            withContainer("Action Title", UIBackgroundStyle.Highlight, (desc, tables, iccc, mutatingScreenKind) => {
                const actions: Description[] = [];
                function addAction(title: string, action: ActionDescription | undefined) {
                    if (action === undefined) return;
                    actions.push(makeActionWithTitle(title, action) as any);
                }
                addAction("Add", iccc.makeFormActionForTables(makeInputOutputTables(tables.input)));
                addAction("Edit", iccc.makeAction(ActionKind.PushEditScreen, tables, mutatingScreenKind));
                addAction("Go!", {
                    kind: ActionKind.ShowToast,
                });
                return {
                    ...desc,
                    subtitle: undefined,
                    image: undefined,
                    actions: makeArrayProperty(actions),
                };
            }),
        ],
    ]);
export type WireTitleComponent = WirePropertiesOfComponent<typeof Title>;

export type HeroSectionDescription = DescriptionOfComponent<typeof Title> & {
    // This is just a marker that we use to tag the Glide-generated Title
    // component in the user profile screen.
    readonly isDefaultUserProfileComponent?: PropertyDescription;
};

const Buttons = component(WireComponentKind.DynamicButtonBar)
    .withIcon("mt-component-button-bar")
    .withDisplayName("Button Block")
    .withDescription("A small block of buttons")
    .withDocURL("pagesButtons")
    .withDescription("A block of buttons with title/subtitle")
    .withProperties(
        properties
            .withArray("buttons", makeActionWithTitleProperties(), {
                propertySection: PropertySection.Action,
                style: ArrayPropertyStyle.ActionArray,
                removeIfMissing: removeIfMissingInActionWithTitle,
                defaultValue: [
                    {
                        title: makeStringProperty("Primary"),
                        action: makeActionProperty({
                            kind: ActionKind.OpenLinkWithArgs,
                            link: makeStringProperty("https://www.glideapps.com"),
                        } as ActionDescription),
                    },
                    {
                        title: makeStringProperty("Secondary"),
                        action: makeActionProperty({
                            kind: ActionKind.ShowToast,
                        }),
                    },
                ] as unknown as readonly Description[],
            })
            .withEnum("alignment", [UIAlignment.Start, UIAlignment.Center, UIAlignment.End] as const, {
                defaultValue: UIAlignment.Center,
            })
            .withText("title", { emptyByDefault: true })
            .withText("description", { emptyByDefault: true })
            .withEnum("size", [ButtonBarSize.Auto, ButtonBarSize.Wide] as const, { defaultValue: ButtonBarSize.Wide })
    )
    .withSpecialCases(true, [
        [
            {
                name: "Button",
                description: "A button",
                img: "mt-component-button",
                group: "Actions",
                appKinds: AppKind.Page,
            },
            desc => ({
                ...desc,
                builderDisplayName: "Button",
                buttons: makeArrayProperty([
                    {
                        title: makeStringProperty("Go!"),
                        action: makeActionProperty({
                            kind: ActionKind.ShowToast,
                        }),
                        alignment: makeEnumProperty(UIAlignment.Start),
                        size: makeEnumProperty(ButtonBarSize.Auto),
                    },
                ]),
            }),
        ],
    ]);
export type WireButtonsComponent = WirePropertiesOfComponent<typeof Buttons>;

export const Breadcrumbs = component(WireComponentKind.Breadcrumbs)
    .withIcon("mt-component-breadcrumbs")
    .withDisplayName("Breadcrumbs")
    .withDescription("Help your user know where they are in your site")
    .withScreenTitle();

const ContactForm = form(WireComponentKind.ContactForm)
    .withDisplayName("Contact Form")
    .withDescription("Collect contact information")
    .withDocURL("pagesContactForm")
    .withProperties(
        properties.withText("title", { defaultValue: "Contact us" }).withText("description", { emptyByDefault: true })
    )
    .withFormProperties(
        properties
            .withText("name")
            .withText("email", { preferredType: "email-address" })
            .withText("phone", { preferredType: "phone-number", emptyByDefault: true })
            .withText("message")
    );
export type WireContactFormComponent = WirePropertiesOfComponent<typeof ContactForm>;

const Fields = component(WireComponentKind.Fields)
    .withDisplayName("Fields")
    .withDescription("Display a collection of data in columns")
    .withIcon("mt-component-fields")
    .withDocURL("pagesFields")
    .withProperties(
        properties
            .withCaption("title", undefined, { emptyByDefault: true })
            .withArray("fields", properties.withCaption("name").withPrimitive("value", { isDefaultCaption: true }), {
                style: ArrayPropertyStyle.KeyValue,
            })
    );
export type WireFieldsComponent = WirePropertiesOfComponent<typeof Fields>;

const BigNumbers = component(WireComponentKind.BigNumbers)
    .withIcon("mt-component-big-numbers")
    .withDisplayName("Big Numbers")
    .withDescription("Numbers but bigger than most")
    .withDocURL("pagesBigNumbers")
    .withProperties(
        properties
            .withCaption("title", undefined)
            .withArray(
                "data",
                properties
                    .withCaption("name")
                    .withPrimitive("value", { isDefaultCaption: true })
                    .withPrimitive("description", { emptyByDefault: true }),
                {
                    style: ArrayPropertyStyle.Card,
                }
            )
    );
export type WireBigNumbersComponent = WirePropertiesOfComponent<typeof BigNumbers>;

const RichText = component(WireComponentKind.RichText)
    .withIcon("mt-component-rich-text")
    .withDisplayName("Rich Text")
    .withDescription("Display text with formatting and images using markdown")
    .withDocURL("pagesRichText")
    .withProperties(
        properties.withText("text", {
            defaultValue:
                "# Header\n\nLorem ipsum dolor sit amet, consectetur adipiscing elit. Venenatis enim vitae quam curabitur id.\n\n- One\n- Two\n- Three",
        })
    );
export type WireRichTextComponent = WirePropertiesOfComponent<typeof RichText>;

const Image = component(WireComponentKind.Image)
    .withIcon("mt-component-image")
    .withDisplayName("Image Block")
    .withDescription("An image with optional title and caption")
    .withDocURL("pagesImage")
    .withProperties(
        properties
            .withText("title", {
                emptyByDefault: true,
                subcomponent: {
                    important: true,
                    name: "Text",
                },
            })
            .withImage("image", {
                subcomponent: {
                    important: true,
                    name: "Image",
                },
            })
            .withText("caption", {
                emptyByDefault: true,
                subcomponent: {
                    important: true,
                    name: "Text",
                },
            })
            .withEnum(
                "aspectRatio",
                [
                    {
                        label: "Auto",
                        value: UIAspect.Auto,
                        icon: "borderOriginal",
                    },
                    {
                        label: "Square",
                        value: UIAspect.Square,
                        icon: "borderSquare",
                    },
                    {
                        label: "4:3",
                        value: UIAspect.FourByThree,
                        icon: "border4by3",
                    },
                    {
                        label: "16:9",
                        value: UIAspect.SixteenByNine,
                        icon: "border3by2", //FIXME
                    },
                    {
                        label: "3:1",
                        value: UIAspect.ThreeByOne,
                        icon: "border3by1",
                    },
                ],
                {
                    enumVisual: "small-images",
                    subcomponent: {
                        important: true,
                        name: "Image",
                    },
                }
            )
            .withEnum(
                "size",
                [
                    { label: "Small", value: UISize.Small },
                    { label: "Medium", value: UISize.Medium },
                    { label: "Large", value: UISize.Large },
                    { label: "Full", value: UISize.Full },
                ],
                {
                    when: d =>
                        getSourceColumnProperty(d.title) !== undefined ||
                        !isEmptyOrUndefined(getStringProperty(d.title)) ||
                        getSourceColumnProperty(d.caption) !== undefined ||
                        !isEmptyOrUndefined(getStringProperty(d.caption)),
                    subcomponent: {
                        important: true,
                        name: "Image",
                    },
                }
            )
    );
export type WireImageComponent = WirePropertiesOfComponent<typeof Image>;

const Video = component(WireComponentKind.Video)
    .withIcon("mt-component-video")
    .withDisplayName("Video")
    .withDescription("Display a video")
    .withDocURL("pagesVideo")
    .withProperties(
        properties
            .withText("title", { emptyByDefault: true })
            .withVideo("video", { defaultValue: "https://www.youtube.com/watch?v=eqxP0DnJlBI" })
            .withText("caption", { emptyByDefault: true })
            .withEnum(
                "aspectRatio",
                [
                    {
                        label: "Square",
                        value: UIAspect.Square,
                        icon: "borderSquare",
                    },
                    {
                        label: "4:3",
                        value: UIAspect.FourByThree,
                        icon: "border4by3",
                    },
                    {
                        label: "16:9",
                        value: UIAspect.SixteenByNine,
                        icon: "border3by2", //FIXME
                    },
                    {
                        label: "3:1",
                        value: UIAspect.ThreeByOne,
                        icon: "border3by1",
                    },
                ],
                {
                    enumVisual: "small-images",
                    defaultValue: UIAspect.SixteenByNine,
                }
            )
            .withEnum(
                "size",
                [
                    { label: "Small", value: UISize.Small },
                    { label: "Medium", value: UISize.Medium },
                    { label: "Large", value: UISize.Large },
                    { label: "Full", value: UISize.Full },
                ],
                {
                    when: d =>
                        getSourceColumnProperty(d.title) !== undefined ||
                        !isEmptyOrUndefined(getStringProperty(d.title)) ||
                        getSourceColumnProperty(d.caption) !== undefined ||
                        !isEmptyOrUndefined(getStringProperty(d.caption)),
                }
            )
    );
export type WireVideoComponent = WirePropertiesOfComponent<typeof Video>;

const Separator = component(WireComponentKind.Separator)
    .withDisplayName("Separator")
    .withDocURL("pagesSeparator")
    .withDescription("Add padding with optional line")
    .withIcon("mt-component-separator")
    .withProperties(
        properties
            .withEnum("size", [SeparatorSize.Small, SeparatorSize.Medium, SeparatorSize.Large])
            .withSwitch("drawLine", true)
    );
export type WireSeparatorComponent = WirePropertiesOfComponent<typeof Separator>;

const Custom = component(WireComponentKind.Custom)
    .withDisplayName("Custom")
    .withIcon("mt-component-custom")
    .withDescription("Create a custom HTML component. This is an experimental feature.")
    .withFeatureSetting("customCodeComponent")
    .withProperties(
        properties
            .withArray("fields", properties.withText("name").withPrimitive("value"))
            .withEnum(
                "source",
                [
                    {
                        label: "URL",
                        value: CustomComponentSource.URL as CustomComponentSource,
                    },
                    {
                        label: "Code",
                        value: CustomComponentSource.InlineCode,
                    },
                ],
                {
                    enumVisual: "dropdown",
                    defaultValue: CustomComponentSource.URL,
                    propertySection: PropertySection.Source,
                }
            )
            .withEnum(
                "sourceTransform",
                [
                    {
                        label: "HTML",
                        value: CustomComponentSourceTransform.HTML as CustomComponentSourceTransform,
                    },
                    {
                        label: "React",
                        value: CustomComponentSourceTransform.React,
                    },
                ],
                {
                    enumVisual: "dropdown",
                    defaultValue: CustomComponentSourceTransform.HTML,
                    propertySection: PropertySection.Source,
                    when: d => getEnumProperty(d.source) === CustomComponentSource.InlineCode,
                    displayName: "Language",
                }
            )
            .withEnum(
                "width",
                [
                    {
                        label: "Automatic",
                        value: CustomComponentWidth.Container as CustomComponentWidth,
                        icon: "paddingLoose",
                    },
                    {
                        label: "Original",
                        value: CustomComponentWidth.Full,
                        icon: "borderOriginal",
                    },
                ],
                {
                    enumVisual: "small-images",
                    defaultValue: CustomComponentWidth.Container,
                    propertySection: PropertySection.Design,
                    displayName: "Responsiveness",
                }
            )
            .withURI("url", {
                displayName: "URL",
                when: d => getEnumProperty(d.source) === CustomComponentSource.URL,
                propertySection: PropertySection.Source,
            })
            .withText("code", {
                syntaxMode: "html",
                allowColumn: false,
                when: d => getEnumProperty(d.source) === CustomComponentSource.InlineCode,
                propertySection: PropertySection.Source,
            })
    );

export type WireCustomComponent = WirePropertiesOfComponent<typeof Custom>;

// `group` helps us visually create named component groups in the definition
// of `components` below.
function group<T>(groupName: string, components: T): T {
    const withinGroup: any = { ...components };
    for (const [k, v] of Object.entries(withinGroup)) {
        withinGroup[k] = (v as any).withinGroup(groupName);
    }
    return withinGroup;
}

// The order of these components determines their order in the product
export const components = {
    ...group("Content", {
        Title,
        // Collection, // from Figma mockup
        // Table, // from Figma mockup
        Fields,
        Image,
        Video,
        BigNumbers,
        // Map, // from Figma mockup,
        Embed: Custom,
    }),
    ...group("Layout", {
        Container,
        // Columns, // from Figma mockup
        Separator,
    }),
    ...group("Text", {
        // Document, // from Figma mockup
        RichText,
        // Text, // from Figma mockup
        Hint,
    }),
    ...group("Actions", {
        Breadcrumb: Breadcrumbs,
        Buttons,
        // Chips, // from Figma mockup
        Button, // from Figma mockup
    }),
    ...group("Forms", {
        ContactForm,
        FormContainer,
    }),
};

export const CardCollection = arrayContent(ArrayScreenFormat.CardCollection)
    .withDisplayName("Collection")
    .withComponentProperties(
        properties
            .withEnum(
                "cardStyle",
                [
                    { value: CardStyle.Card, label: "Card", icon: "cards" },
                    { value: CardStyle.Cover, label: "Grid", icon: "columns" },
                    { value: CardStyle.List, label: "List", icon: "list" },
                    { value: CardStyle.Table, label: "Table", icon: "table" },
                    // { value: CardStyle.Article, label: "Article", icon: "componentInlineList" },
                ],
                {
                    propertySection: PropertySection.Style,
                    enumVisual: "large-images",
                }
            )
            .withEnum(
                "size",
                [
                    { value: UISize.XSmall, label: "XSmall" },
                    { value: UISize.Small, label: "Small" },
                    { value: UISize.Medium, label: "Medium" },
                    { value: UISize.Large, label: "Large" },
                    { value: UISize.XLarge, label: "XLarge" },
                ],
                {
                    propertySection: PropertySection.Design,
                    when: d => {
                        const style = getEnumProperty<CardStyle>(d.cardStyle);
                        return style === CardStyle.Card || style === CardStyle.Cover;
                    },
                }
            )
            .withEnum(
                "style",
                [
                    { value: UIStyleVariant.Default, label: "Default" },
                    { value: UIStyleVariant.Minimal, label: "Minimal" },
                ],
                {
                    defaultValue: UIStyleVariant.Default,
                    propertySection: PropertySection.Design,

                    when: d => {
                        const style = getEnumProperty<CardStyle>(d.cardStyle);
                        return style === CardStyle.List || style === CardStyle.Table;
                    },
                }
            )
            .withEnum(
                "imageStyle",
                [
                    {
                        label: "Square",
                        value: UIImageStyle.Rectilinear,
                        icon: "borderSquare",
                    },
                    {
                        label: "Circle",
                        value: UIImageStyle.Circle,
                        icon: "borderCircle",
                    },
                ],
                {
                    defaultValue: UIImageStyle.Rectilinear,
                    propertySection: PropertySection.Design,
                }
            )
            .withEnum(
                "imageSize",
                [
                    { value: UISize.XSmall, label: "XSmall" },
                    { value: UISize.Small, label: "Small" },
                    { value: UISize.Medium, label: "Medium" },
                    { value: UISize.Large, label: "Large" },
                ],
                {
                    defaultValue: UISize.Medium,
                    propertySection: PropertySection.Design,

                    when: d => {
                        const style = getEnumProperty<CardStyle>(d.cardStyle);
                        const imageStyle = getEnumProperty<UIImageStyle>(d.imageStyle);
                        return (
                            (style === CardStyle.Card || style === CardStyle.Cover) &&
                            imageStyle === UIImageStyle.Circle
                        );
                    },
                }
            )
            .withEnum(
                "alignment",
                [
                    { value: UIAlignment.Start, label: "Left" },
                    { value: UIAlignment.Center, label: "Center" },
                ],
                {
                    defaultValue: UIAlignment.Center,
                    propertySection: PropertySection.Design,

                    when: d => {
                        const style = getEnumProperty<CardStyle>(d.cardStyle);
                        const imageStyle = getEnumProperty<UIImageStyle>(d.imageStyle);
                        return (
                            (style === CardStyle.Card || style === CardStyle.Cover) &&
                            imageStyle === UIImageStyle.Circle
                        );
                    },
                }
            )
            .withEnum(
                "aspectRatio",
                [
                    {
                        label: "Square",
                        value: UIAspect.Square,
                        icon: "borderSquare",
                    },
                    {
                        label: "4:3",
                        value: UIAspect.FourByThree,
                        icon: "border4by3",
                    },
                    {
                        label: "3:2",
                        value: UIAspect.ThreeByTwo,
                        icon: "border3by2",
                    },
                    {
                        label: "16:9",
                        value: UIAspect.SixteenByNine,
                        icon: "border3by2", //FIXME
                    },
                ],
                {
                    defaultValue: UIAspect.SixteenByNine,
                    propertySection: PropertySection.Design,
                    when: d => {
                        const style = getEnumProperty<CardStyle>(d.cardStyle);
                        const imageStyle = getEnumProperty<UIImageStyle>(d.imageStyle);
                        return (
                            (style === CardStyle.Card || style === CardStyle.Cover) &&
                            imageStyle !== UIImageStyle.Circle
                        );
                    },
                }
            )
            .withEnum(
                "imageFill",
                [
                    {
                        label: "Fill",
                        value: UIImageFill.Cover,
                    },
                    {
                        label: "Fit",
                        value: UIImageFill.Contain,
                    },
                ],
                {
                    defaultValue: UIImageFill.Cover,
                    propertySection: PropertySection.Design,
                    when: d => {
                        const style = getEnumProperty<CardStyle>(d.cardStyle);
                        return style === CardStyle.Card;
                    },
                }
            )
            .withText("componentTitle", {
                displayName: "Title",
                propertySection: PropertySection.DataTop,
                preferredNames: titleProperties,
            })
            .withArray("titleActions", makeActionWithTitleProperties(), {
                propertySection: PropertySection.TitleBarAction,
                style: ArrayPropertyStyle.ActionArray,
                specialItems: 2,
                allowEmpty: true,
                removeIfMissing: removeIfMissingInActionWithTitle,
            })
            .withText("headerTitle", {
                displayName: "Title",
                propertySection: PropertySection.ColumnHeaders,
                allowColumn: false,
                when: c => getEnumProperty(c?.cardStyle) === CardStyle.Table,
            })
            .withText("headerSubtitle", {
                displayName: "Description",
                propertySection: PropertySection.ColumnHeaders,
                allowColumn: false,
                when: c => getEnumProperty(c?.cardStyle) === CardStyle.Table,
            })
            .withText("headerEmphasis", {
                displayName: "Meta",
                propertySection: PropertySection.ColumnHeaders,
                allowColumn: false,
                when: c => getEnumProperty(c?.cardStyle) === CardStyle.Table,
            })
    )
    .withItemProperties(p =>
        p
            .withText("title", {
                propertySection: PropertySection.CollectionItems,
                subcomponent: {
                    important: true,
                    name: "Content",
                },
            })
            .withText("subtitle", {
                displayName: "Description",
                propertySection: PropertySection.CollectionItems,
                subcomponent: {
                    important: true,
                    name: "Content",
                },
            })
            .withText("emphasis", {
                preferredNames: titleProperties,
                propertySection: PropertySection.CollectionItems,
                displayName: "Meta",
                emptyByDefault: true,
                subcomponent: {
                    important: true,
                    name: "Content",
                },
            })
            .withText("body", {
                // The card style isn't really in the parent description - see
                // ##whenInArrayComponentItems.
                when: (_d, c) => getEnumProperty(c?.cardStyle) === CardStyle.Article,
                propertySection: PropertySection.CollectionItems,
                preferredNames: [],
                displayName: "Content",
                emptyByDefault: true,
                subcomponent: {
                    important: true,
                    name: "Content",
                },
            })
            .withImage("image", {
                propertySection: PropertySection.CollectionItems,
                subcomponent: {
                    important: true,
                    name: "Image",
                },
            })
            .withAction("action", {
                propertySection: PropertySection.ItemClick,
                emptyByDefault: false,
                subcomponent: {
                    important: true,
                    name: "Actions",
                },
            })
            .withArray("itemActions", makeActionWithTitleProperties(), {
                propertySection: PropertySection.CollectionItemActions,
                style: ArrayPropertyStyle.ActionArray,
                specialItems: 2,
                allowEmpty: true,
                removeIfMissing: removeIfMissingInActionWithTitle,
                subcomponent: {
                    important: true,
                    name: "Actions",
                },
                addItemLabels: ["Add primary action", "Add secondary action", "Add dropdown action"],
            })
    )
    .withSpecialCases([
        [
            {
                name: "Card",
                description: "A collection of cards",
                img: "mt-component-collection-cards",
                group: "Collections",
                appKinds: AppKind.Page,
            },
            desc => ({ ...desc, cardStyle: makeEnumProperty(CardStyle.Card) }),
        ],
        [
            {
                name: "Grid",
                description: "A grid of data",
                img: "mt-component-collection-grid",
                group: "Collections",
                appKinds: AppKind.Page,
            },
            desc => ({ ...desc, cardStyle: makeEnumProperty(CardStyle.Cover) }),
        ],
        [
            {
                name: "List",
                description: "A list",
                img: "mt-component-collection-list",
                group: "Collections",
                appKinds: AppKind.Page,
            },
            desc => ({ ...desc, cardStyle: makeEnumProperty(CardStyle.List) }),
        ],
        [
            {
                name: "Table",
                description: "A table",
                img: "mt-component-collection-table",
                group: "Collections",
                appKinds: AppKind.Page,
            },
            (desc, tables, itemTable, adc, mutatingScreenKind) => {
                const iccc = promoteAppDescriptionContext(adc);
                const titleActions: Description[] = [];
                const itemActions: Description[] = [];
                if (itemTable !== undefined && iccc !== undefined) {
                    const addAction = iccc.makeFormActionForTables(makeInputOutputTables(tables.input, itemTable));
                    if (addAction !== undefined) {
                        titleActions.push(makeActionWithTitle("Add", addAction));
                    }

                    const itemTables = makeInputOutputTables(itemTable);
                    const editAction = iccc.makeAction(
                        ActionKind.PushEditScreen,
                        itemTables,
                        // TODO: Does this even apply here, since it's an action in the inline list?
                        mutatingScreenKind
                    );
                    if (editAction !== undefined) {
                        itemActions.push(makeActionWithTitle("Edit", editAction));
                    }

                    let showAction = iccc.makeAction(
                        ActionKind.PushDetailScreen,
                        itemTables,
                        // TODO: Does this even apply here, since it's an action in the inline list?
                        mutatingScreenKind
                    );
                    if (showAction?.kind === ActionKind.PushDetailScreen) {
                        showAction = {
                            ...showAction,
                            navigationTarget: makeEnumProperty(PageScreenTarget.LargeModal),
                        } as ActionDescription;
                        itemActions.push(makeActionWithTitle("Quick look", showAction));
                    }
                }
                return {
                    ...desc,
                    cardStyle: makeEnumProperty(CardStyle.Table),
                    allowSearch: makeSwitchProperty(true),
                    titleActions: makeArrayProperty(titleActions),
                    itemActions: makeArrayProperty(itemActions),
                };
            },
        ],
    ]);
export type CardCollectionComponentDescription = DescriptionOfArrayContentInlineList<typeof CardCollection>;
export type WireListCardCollection = WirePropertiesOfArrayContent<typeof CardCollection>;

export const dataGridColumnProperties = properties
    .withCaption("title", "Title", { allowColumn: false })
    .withText("value", { isDefaultCaption: true });

export const DataGrid = arrayContent(ArrayScreenFormat.DataGrid)
    .withItemProperties(p => p.withArray("columns", dataGridColumnProperties, { style: ArrayPropertyStyle.KeyValue }))
    .withSpecialCases([
        [
            {
                name: "Data Grid",
                analyticsName: "data-grid",
                description: "A grid of data",
                img: "componentCharts",
                group: "Lists",
                appKinds: AppKind.Page,
                isLegacy: true,
            },
            desc => desc,
        ],
    ]);
